import React, {  lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/experiences/ProjectsExperience.module.scss";

import PromodoCover from "../../assets/Promodo.png"
import WeatherCover from "../../assets/Weather.png"
import MusicCover from "../../assets/Music.png"
import EcomCover from "../../assets/Shop.png"
import PoliticsCover from "../../assets/Politics.png"
import MazionCover from "../../assets/Mazion.png"

const TAGS = ["All", "Product", "Media", "Entertainment", "E-comm"];

const Pomodoro = lazy(() => import("./Projects/Pomodoro"));
const Weather = lazy(() => import("./Projects/Weather"));
const Music = lazy(() => import("./Projects/Music"));
const Shop = lazy(() => import("./Projects/Shop"));
const GovHotwire = lazy(() => import("./Projects/GovHotwire"));

const PROJECTS = [
    {
    id: "p1",
    title: "Politics News",
    thumb: PoliticsCover,
    tags: ["politics","Media","real-rime"],
    details: "Stay updated on current political news and issues.",
    component: GovHotwire                // <-- key difference
  },
  { id: "p2",
    title: "Mazion Entertainment News",
    thumb: MazionCover, tags: ["News", "Entertainment", "Media"],
    demoUrl: "/news-project.gif",
    details: "Case Study: Increased creator throughput by simplifying asset flows and adding smart templates. Stack: Figma, TS, React."
   },
 {
    id: "p3",
    title: "Top 100 Billboard",
    thumb: MusicCover,
    tags: ["Music","Entertainment","Billboard"],
    details: "Top 100 musics in different countries.",
    component: Music                // <-- key difference
  },
  {
    id: "p4",
    title: "Pomodoro + Tasks",
    thumb: PromodoCover,
    tags: ["Product","task","planner"],
    details: "Interact with the Pomodoro timer + task list. React + CSS Modules.",
    component: Pomodoro                // <-- key difference
  },
  {
    id: "p5",
    title: "Shop E-com",
    thumb: EcomCover,
    tags: ["Retail","Shop","E-comm"],
    details: "Shop here, your ultimate site.",
    component: Shop                // <-- key difference
  },
  {
    id: "p6",
    title: "Weather App",
    thumb: WeatherCover,
    tags: ["Weather","Climate","Product"],
    details: "Check climates and weathers.",
    component: Weather                // <-- key difference
  },
];

// const EXPERIENCE = [
//   { role: "Independent Builder", org: "Studio", range: "2023 — Present", bullets: ["Strategy → shipped for early teams", "Rapid prototypes and automation", "Calm plans, clear comms"] },
//   { role: "Product Engineer", org: "Various", range: "2019 — 2023", bullets: ["Full-stack build on web", "UX + design systems", "Integrations and ops tooling"] },
//   { role: "Community & Mentorship", org: "LA Tech", range: "Ongoing", bullets: ["Inclusive events", "Career guidance", "Workshops"] }
// ];

const EXPERIENCE = [
  {
    role: "Lead Product Engineer",
    org: "RitualAds",
    range: "Nov 2024 — Present",
    bullets: [
      "SaaS service architecture development.",
      "Collaborating directly with C-suite to meet user and stakeholder requirements.",
      "Advertising tech startup serving commercial and agency clients.",
    ],
  },
  {
    role: "Full-Stack Engineer Intern",
    org: "SBTDC",
    range: "May 2024 — Aug 2024",
    bullets: [
      "Developed e‑commerce sites for partner business clients.",
      "Implemented inventory workflows, SEOs, and Technical Documentations."
    ],
  },
  {
    role: "Founder & Program Coordinator",
    org: "Stardov",
    range: "Oct 2021 — Mar 2023",
    bullets: [
      "E-commerce storefront and end-to-end product sourcing, inventory & fulfillment.",
      "Ops leadership, managed digital marketing strategy."
    ],
  },
  {
    role: "Founder",
    org: "Eklektos Mumtaz",
    range: "Apr 2020 — Nov 2022",
    bullets: [
      "Smart fashion venture and curated consumer goods.",
      "Direct-to-consumer brand in premium accessories",
      "Brand assets, QC, logistics, and e-commerce stack."
    ],
  },
];


const SKILLS = [
  "Program/Product Management", "Business Operation", "Venture Partnerships", "Product strategy", "Information architecture", "UX Research", "Community Engagement", "Design systems", "Prototyping (Figma)",
  "TypeScript", "React", "Node", "APIs", "Postgres", "Automation (Zapier/Make)"
];

export default function ProjectsExperience() {
  const [activeTag, setActiveTag] = useState("All");
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("projects");
  const [modalContent, setModalContent] = useState({ title:"", details:"", demoUrl:"", poster:"" });
const [activeProject, setActiveProject] = useState(null);

  const videoRef = useRef(null);

  const heroRef = useRef(null);
  const spotRef = useRef(null);
  const dialogRef = useRef(null);

  const projectsRef = useRef(null);
  const experienceRef = useRef(null);
  const skillsRef = useRef(null);

  // Theme init + toggle (matches original logic)
  useEffect(() => {
    const prefersLight = window.matchMedia("(prefers-color-scheme: light)").matches;
    const saved = localStorage.getItem("theme");
    if (saved === "light" || (!saved && prefersLight)) {
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    document.documentElement.classList.toggle("light");
    localStorage.setItem(
      "theme",
      document.documentElement.classList.contains("light") ? "light" : "dark"
    );
  };

  // Scroll progress
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = Math.max(0, Math.min(1, (window.scrollY || 0) / Math.max(1, h)));
      setProgress(pct);
    };
    document.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => document.removeEventListener("scroll", onScroll);
  }, []);

  // Hero spotlight
  const onHeroPointerMove = (e) => {
    const hero = heroRef.current;
    const spot = spotRef.current;
    if (!hero || !spot) return;
    const r = hero.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    spot.style.setProperty("--mx", `${x}%`);
    spot.style.setProperty("--my", `${y}%`);
  };

  // Section highlight via IntersectionObserver
  useEffect(() => {
    const pairs = [
      { id: "projects", el: projectsRef.current },
      { id: "experience", el: experienceRef.current },
      { id: "skills", el: skillsRef.current }
    ].filter(Boolean);

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveSection(en.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0.01 }
    );

    pairs.forEach(({ el }) => el && obs.observe(el));
    return () => {
      pairs.forEach(({ el }) => el && obs.unobserve(el));
      obs.disconnect();
    };
  }, []);

  // Filtered projects
  const filtered = useMemo(
    () => PROJECTS.filter((p) => activeTag === "All" || p.tags.includes(activeTag)),
    [activeTag]
  );


const openProject = (p) => {
  setActiveProject(p);
  dialogRef.current?.showModal?.();
};

const closeProject = () => {
  try { if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; } } catch {}
  setActiveProject(null);           // ensures the interactive demo unmounts
  dialogRef.current?.close?.();
};


// helpers (above return)
const isExternalVideo = (url = "") => /youtube\.com|youtu\.be|vimeo\.com/.test(url);
const isVideoFile     = (url = "") => /\.(mp4|webm|ogg)$/i.test(url);
const isGif           = (url = "") => /\.gif$/i.test(url);
const toEmbedUrl = (url = "") => {
  if (url.includes("youtu.be")) return `https://www.youtube.com/embed/${url.split("/").pop().split("?")[0]}`;
  if (url.includes("youtube.com/watch")) { const v = new URL(url).searchParams.get("v"); return `https://www.youtube.com/embed/${v}`; }
  if (url.includes("vimeo.com")) return `https://player.vimeo.com/video/${url.split("/").pop()}`;
  return url;
};
  return (
    <div>
      {/* Progress bar */}
      <div className={styles.progress} style={{ width: `${progress * 100}%` }} />

      {/* Nav */}
      <header className={styles.nav} aria-label="Primary">
        <div className={`${styles["nav-inner"]} ${styles.wrap}`}>


          <nav className={styles.links} aria-label="Sections">
            <a href="#projects" id="lnk-projects" aria-current={activeSection === "projects" ? "true" : "false"}>
              Projects
            </a>
            <a href="#experience" id="lnk-exp" aria-current={activeSection === "experience" ? "true" : "false"}>
              Experience
            </a>
            <a href="#skills" id="lnk-skills" aria-current={activeSection === "skills" ? "true" : "false"}>
              Skills
            </a>
            {/* <button onClick={toggleTheme} className={styles.toggle}>Theme</button> */}
          </nav>
        </div>
      </header>

      <main className={styles.wrap}>
        {/* Hero */}
        <section className={`${styles.section} ${styles.hero}`} id="top" ref={heroRef} onPointerMove={onHeroPointerMove}>
          <div className={styles.spot} ref={spotRef} aria-hidden="true" />
          <h1 className={styles.title}>
            Projects &nbsp; <span className={styles.grad}>Experience</span>
          </h1>
          <p className={styles.lede}>
           Take a moment to browse some projects, each one is viewable directly on this site, with no redirects.
           I enjoy building creative, technical work using Python, FastAPI, Data management (SQL),  and JS depending on the project functionality.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a className={`${styles.btn} ${styles.primary}`} href="#projects">Browse projects</a>
            <a className={styles.btn} href="#experience">See experience</a>
            <button className={styles.btn} onClick={() => alert("Not Available Currently - Contact Us")}>
              Download resume (PDF)
            </button>
          </div>
        </section>

        {/* Projects */}
        <section className={styles.section} id="projects" ref={projectsRef}>
          <h2 className={styles.kicker}><span className={styles.dot} /> Projects</h2>
          <div className={styles.panel}>
            <div className={styles.filters} role="toolbar" aria-label="Project filters">
              {TAGS.map((t) => (
                <button
                  key={t}
                  className={styles.chip}
                  aria-pressed={activeTag === t ? "true" : "false"}
                  onClick={() => setActiveTag(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            <div className={styles.grid} style={{ marginTop: 14 }}>
  {filtered.map((p) => (
    <div key={p.id} className={styles["col-4"]}>
      <article className={styles.card} onClick={() => openProject(p)} tabIndex={0}>
        <div
          className={styles.thumb}
          style={{ ["--thumb-img"]: p.thumb ? `url(${p.thumb})` : undefined }}
        />
        <h3 style={{ margin: "8px 0 6px" }}>{p.title}</h3>
        <p style={{ margin: "0 0 10px", color: "var(--muted)" }}>{p.summary}</p>
        <div className={styles.meta}>{p.tags.map(t => <span key={t} className={styles.tag}>{t}</span>)}</div>
      </article>
    </div>
  ))}
</div>
 </div>
      </section>

        {/* Experience */}
        <section className={styles.section} id="experience" ref={experienceRef}>
          <h2 className={styles.kicker}><span className={styles.dot} /> Experience</h2>
          <div className={styles.panel}>
            <ol className={styles.timeline} aria-label="Professional timeline">
              {EXPERIENCE.map((x) => (
                <li key={`${x.role}-${x.org}`} className={styles.t}>
                  <div className={styles["t-dot"]} aria-hidden="true" />
                  <h3 className={styles["t-role"]}>{`${x.role} · ${x.org}`}</h3>
                  <div className={styles["t-sub"]}>{x.range}</div>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "var(--muted)" }}>
                    {x.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Skills */}
        <section className={styles.section} id="skills" ref={skillsRef}>
          <h2 className={styles.kicker}><span className={styles.dot} /> Skills</h2>
          <div className={styles.panel}>
            <div className={styles.skills}>
              {SKILLS.map((k) => (
                <span key={k} className={styles.pill}>{k}</span>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Modal */}
      <dialog ref={dialogRef} className={styles.dialog}>
        <div className={styles.modal}>
          <div className={styles["modal-h"]}>
            <h3 style={{ margin: 0 }}>{modalContent.title}</h3>
            <button className={styles.close} onClick={closeProject}>Close</button>
          </div>

<div className={styles["modal-b"]}>
  {activeProject?.component ? (
    <Suspense fallback={<div className={styles.loading}>Loading…</div>}>
      <div className={styles.embed}>
        {React.createElement(activeProject.component, { key: activeProject.id })}
      </div>
    </Suspense>
  ) : activeProject?.demoUrl ? (
    isExternalVideo(activeProject.demoUrl) ? (
      <div className={styles.mediaWrap}>
        <iframe
          src={toEmbedUrl(activeProject.demoUrl)}
          title={`${activeProject.title} demo`}
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    ) : isVideoFile(activeProject.demoUrl) ? (
      <div className={styles.mediaWrap}>
        <video ref={videoRef} controls playsInline preload="metadata" poster={activeProject.poster || undefined}>
          <source src={activeProject.demoUrl} type="video/mp4" />
        </video>
      </div>
    ) : isGif(activeProject.demoUrl) ? (
      <div className={styles.mediaWrap}>
        <img src={activeProject.demoUrl} alt={`${activeProject.title} demo`} />
      </div>
    ) : (
      <a href={activeProject.demoUrl} target="_blank" rel="noreferrer">Open media</a>
    )
  ) : (
    <div style={{ color: "var(--muted)" }}>{activeProject?.details}</div>
  )}
</div>
        </div>
      </dialog>
    </div>
  );
}
